import { useState, useMemo, useEffect, useRef } from "react";
import { Layout } from "@/components/Layout";
import { PlanGate } from "@/components/PlanGate";
import { REQUIRES } from "@/lib/plans";
import { EXERCISES, MUSCLE_GROUPS } from "@/data/exercises";
import { X, Barbell, Target, Clock, CheckCircle, Warning, Gear, ListChecks, DownloadSimple, Lock, CircleNotch } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { getLevel } from "@/lib/plans";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { buildExerciseVTT } from "@/lib/vtt";

// Placeholder poster (icon-based) for exercises without image
function PlaceholderPoster() {
  return (
    <div className="w-full h-full flex flex-col justify-between p-6 relative overflow-hidden"
         style={{
           background: "linear-gradient(135deg, #0D0D0D 0%, #1A1A1A 100%)",
         }}>
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: "repeating-linear-gradient(45deg, #39FF14, #39FF14 1px, transparent 1px, transparent 20px)",
      }} />
      <div className="relative flex justify-between items-start">
        <div>
          <p className="as-label text-as-green text-[10px] tracking-widest mb-2">AS TRAINING</p>
          <div className="h-[2px] w-12 bg-as-green" />
        </div>
        <span className="as-label text-as-green text-[9px] tracking-widest border border-as-green px-2 py-1">
          FICHA TÉCNICA
        </span>
      </div>
      <div className="relative flex-1 flex items-center justify-center">
        <Barbell size={80} weight="duotone" color="#39FF14" className="opacity-80" />
      </div>
      <div className="relative">
        <div className="flex items-center gap-2 text-as-gray text-[10px] uppercase tracking-widest">
          <span>Ver ejecución</span>
          <span className="text-as-green">→</span>
        </div>
      </div>
    </div>
  );
}

export default function ExerciseGuide() {
  const [group, setGroup] = useState("all");
  const [active, setActive] = useState(null);

  const filtered = useMemo(
    () => (group === "all" ? EXERCISES : EXERCISES.filter((e) => e.muscle_group === group)),
    [group]
  );

  return (
    <Layout>
      <PlanGate required={REQUIRES.EXERCISE_GUIDE} planName="AS START">
      <section className="border-b border-as-border bg-as-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="as-label text-as-green mb-3">GUÍA DE EJERCICIOS</p>
          <h1 className="as-heading text-4xl sm:text-5xl lg:text-6xl text-white" data-testid="exercises-title">
            TÉCNICA <span className="text-as-green">CORRECTA.</span>
          </h1>
          <p className="text-as-gray mt-4 max-w-xl">
            Catálogo de ejercicios fundamentales con técnica, músculos trabajados, beneficios y errores comunes.
          </p>
          <div className="flex gap-6 mt-6 text-xs text-as-gray">
            <span><span className="text-white font-bold">{EXERCISES.length}</span> ejercicios</span>
            <span><span className="text-white font-bold">{MUSCLE_GROUPS.length - 1}</span> grupos musculares</span>
          </div>
        </div>
      </section>

      <section className="bg-as-black sticky top-16 z-30 border-b border-as-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex gap-2 flex-wrap">
          {MUSCLE_GROUPS.map((m) => {
            const count = m.id === "all" ? EXERCISES.length : EXERCISES.filter(e => e.muscle_group === m.id).length;
            return (
              <button
                key={m.id}
                onClick={() => setGroup(m.id)}
                className={`px-4 py-2 text-xs uppercase tracking-wider font-bold transition-colors ${
                  group === m.id
                    ? "bg-as-green text-as-black"
                    : "bg-as-surface text-white border border-as-border hover:border-as-green"
                }`}
                data-testid={`ex-filter-${m.id}`}
              >
                {m.label} <span className="opacity-60">({count})</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="bg-as-surface py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((ex) => (
              <div
                key={ex.id}
                onClick={() => setActive(ex)}
                className="bg-as-black border border-as-border hover:border-as-green cursor-pointer transition-all group relative"
                data-testid={`exercise-${ex.id}`}
              >
                {ex.video_url && (
                  <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 bg-as-green text-as-black text-[9px] font-bold uppercase tracking-widest px-2 py-1 shadow-lg" data-testid={`video-badge-${ex.id}`}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                    VIDEO
                  </div>
                )}
                <div className="aspect-square bg-as-black overflow-hidden">
                  {ex.poster_url ? (
                    <img
                      src={ex.poster_url}
                      alt={ex.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <PlaceholderPoster />
                  )}
                </div>
                <div className="p-4">
                  <h3 className="as-heading text-lg text-white mb-1">{ex.name.toUpperCase()}</h3>
                  <p className="as-label text-as-green text-[10px]">
                    {ex.tags.join(" · ")}
                  </p>
                </div>
              </div>
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-16 text-as-gray">
              <p>Sin ejercicios en este grupo aún.</p>
            </div>
          )}
        </div>
      </section>

      {active && (
        <ExerciseModal exercise={active} onClose={() => setActive(null)} />
      )}
      </PlanGate>
    </Layout>
  );
}

function ExerciseVideo({ exercise }) {
  const videoRef = useRef(null);
  const [trackUrl, setTrackUrl] = useState(null);
  const hasSteps = Array.isArray(exercise.execution) && exercise.execution.length > 0;

  // Build the VTT track once the video reports its real duration, and revoke
  // the object-URL when the modal closes / exercise changes to avoid leaks.
  useEffect(() => {
    if (!hasSteps) return undefined;
    let url = null;

    const onLoaded = () => {
      const d = videoRef.current?.duration;
      if (Number.isFinite(d) && d > 0) {
        url = buildExerciseVTT(exercise.execution, d);
        if (url) setTrackUrl(url);
      }
    };

    const el = videoRef.current;
    el?.addEventListener("loadedmetadata", onLoaded);
    return () => {
      el?.removeEventListener("loadedmetadata", onLoaded);
      if (url) URL.revokeObjectURL(url);
    };
  }, [exercise.execution, hasSteps]);

  return (
    <video
      ref={videoRef}
      src={exercise.video_url}
      poster={exercise.poster_url || undefined}
      controls
      autoPlay
      muted
      loop
      playsInline
      crossOrigin="anonymous"
      aria-label={`Video demostrativo de técnica correcta: ${exercise.name}`}
      className="w-full h-auto max-h-[60vh] object-contain bg-black"
      data-testid={`modal-video-${exercise.id}`}
    >
      {trackUrl && (
        <track
          kind="captions"
          src={trackUrl}
          srcLang="es"
          label="Español (descripción de la técnica)"
          default
        />
      )}
      Tu navegador no soporta el reproductor de video.
    </video>
  );
}


function ExerciseModal({ exercise, onClose }) {
  const hasPoster = Boolean(exercise.poster_url);
  const hasVideo = Boolean(exercise.video_url);
  const hasDetails = Boolean(exercise.execution && exercise.execution.length > 0);

  // Close on Escape (WCAG 2.1.2 No Keyboard Trap)
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="exercise-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exercise-modal-title"
    >
      <div
        className="bg-as-black border-2 border-as-green max-w-4xl w-full my-4 sm:my-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-as-border flex justify-between items-center gap-3 sticky top-0 bg-as-black z-10">
          <div className="min-w-0 flex-1">
            <p className="as-label text-as-green text-[10px] mb-1">
              {MUSCLE_GROUPS.find(g => g.id === exercise.muscle_group)?.label || "EJERCICIO"}
            </p>
            <h2 className="as-heading text-lg sm:text-xl text-white truncate" id="exercise-modal-title">{exercise.name.toUpperCase()}</h2>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <DownloadPdfButton exercise={exercise} />
            <button onClick={onClose} className="text-white hover:text-as-green transition-colors" data-testid="exercise-modal-close">
              <X size={24} weight="bold" />
            </button>
          </div>
        </div>

        {/* Video (técnica en movimiento) — cuando esté disponible tiene prioridad sobre el póster */}
        {hasVideo && (
          <div className="bg-black relative">
            <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 bg-as-green text-as-black text-[9px] font-bold uppercase tracking-widest px-2 py-1">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
              TÉCNICA EN MOVIMIENTO
            </div>
            <ExerciseVideo exercise={exercise} />
          </div>
        )}

        {/* Póster (ficha completa) — se muestra debajo del video cuando existe */}
        {hasPoster ? (
          <img
            src={exercise.poster_url}
            alt={exercise.name}
            className="w-full h-auto"
            data-testid={`modal-poster-${exercise.id}`}
          />
        ) : hasDetails ? (
          <div className="p-4 sm:p-6 space-y-6">
            {/* Tags header */}
            <div className="pb-4 border-b border-as-border">
              <p className="as-label text-as-green text-xs tracking-widest">
                {exercise.tags.join(" • ").toUpperCase()}
              </p>
            </div>

            {/* Ejecución + Músculos + Beneficios */}
            <div className="grid md:grid-cols-3 gap-6">
              <TechniqueBlock
                icon={<Gear size={20} weight="duotone" color="#39FF14" />}
                title="EJECUCIÓN"
                items={exercise.execution}
              />
              <TechniqueBlock
                icon={<Target size={20} weight="duotone" color="#39FF14" />}
                title="MÚSCULOS TRABAJADOS"
                items={exercise.muscles}
                variant="pill"
              />
              <TechniqueBlock
                icon={<CheckCircle size={20} weight="duotone" color="#39FF14" />}
                title="BENEFICIOS"
                items={exercise.benefits}
              />
            </div>

            {/* Claves Técnicas + Errores */}
            <div className="grid md:grid-cols-2 gap-6 pt-4 border-t border-as-border">
              <div className="bg-as-surface p-4 border-l-2 border-as-green">
                <div className="flex items-center gap-2 mb-3">
                  <ListChecks size={18} weight="duotone" color="#39FF14" />
                  <h4 className="as-heading text-sm text-as-green">CLAVES TÉCNICAS</h4>
                </div>
                <ul className="space-y-2">
                  {exercise.keys.map((k, i) => (
                    <li key={i} className="text-xs text-white leading-relaxed flex gap-2">
                      <CheckCircle size={14} weight="fill" color="#39FF14" className="flex-shrink-0 mt-0.5" />
                      <span>{k}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-as-surface p-4 border-l-2" style={{ borderColor: "#FF3333" }}>
                <div className="flex items-center gap-2 mb-3">
                  <Warning size={18} weight="duotone" color="#FF3333" />
                  <h4 className="as-heading text-sm" style={{ color: "#FF3333" }}>ERRORES COMUNES</h4>
                </div>
                <ul className="space-y-2">
                  {exercise.errors.map((k, i) => (
                    <li key={i} className="text-xs text-white leading-relaxed flex gap-2">
                      <X size={14} weight="bold" color="#FF3333" className="flex-shrink-0 mt-0.5" />
                      <span>{k}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Series / Reps / Descanso */}
            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-as-border">
              <StatBox icon={<Barbell size={20} weight="duotone" color="#39FF14" />} label="SERIES" value={exercise.sets} />
              <StatBox icon={<Target size={20} weight="duotone" color="#39FF14" />} label="REPS" value={exercise.reps} />
              <StatBox icon={<Clock size={20} weight="duotone" color="#39FF14" />} label="DESCANSO" value={exercise.rest} />
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-as-gray">
            <p>Detalles próximamente.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function TechniqueBlock({ icon, title, items, variant = "list" }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h4 className="as-heading text-sm text-as-green">{title}</h4>
      </div>
      {variant === "pill" ? (
        <div className="flex flex-wrap gap-2">
          {items.map((m, i) => (
            <span key={i} className="text-[11px] px-2 py-1 bg-as-surface text-white border border-as-border">
              {m}
            </span>
          ))}
        </div>
      ) : (
        <ol className="space-y-2">
          {items.map((step, i) => (
            <li key={i} className="text-xs text-as-gray leading-relaxed flex gap-2">
              <span className="text-as-green font-bold flex-shrink-0">{i + 1}.</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function StatBox({ icon, label, value }) {
  return (
    <div className="bg-as-surface p-3 sm:p-4 border border-as-border flex flex-col items-center text-center">
      <div className="mb-2">{icon}</div>
      <p className="as-label text-as-green text-[10px] mb-1">{label}</p>
      <p className="text-white font-bold text-xs sm:text-sm">{value}</p>
    </div>
  );
}

function DownloadPdfButton({ exercise }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const level = getLevel(user);
  const canDownload = level >= 2; // EVOLUTION (2) or ELITE (3) or admin (99)

  const handleDownload = async () => {
    if (!user) {
      toast.error("Inicia sesión para descargar la ficha", {
        description: "Necesitas una cuenta activa con plan ELITE o EVOLUTION",
      });
      return;
    }
    if (!canDownload) {
      toast.error("Función exclusiva ELITE / EVOLUTION", {
        description: "Actualiza tu plan para descargar fichas técnicas en PDF",
      });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/exercises/pdf`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(exercise),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Error al generar PDF" }));
        throw new Error(err.detail || "Error al generar PDF");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ficha_${exercise.id}_AS_TRAINING.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF descargado", { description: exercise.name });
    } catch (e) {
      toast.error(e.message || "No pudimos generar el PDF");
    } finally {
      setLoading(false);
    }
  };

  // Locked state (Basic plan or not logged in)
  if (!canDownload) {
    return (
      <button
        onClick={handleDownload}
        className="hidden sm:flex items-center gap-2 px-3 py-2 bg-as-surface border border-as-border text-as-gray hover:border-as-green hover:text-as-green transition-colors text-[10px] uppercase tracking-wider font-bold"
        data-testid="pdf-download-locked"
        title={user ? "Requiere plan ELITE o EVOLUTION" : "Inicia sesión con plan ELITE o EVOLUTION"}
      >
        <Lock size={14} weight="bold" />
        <span className="hidden md:inline">PDF ELITE</span>
      </button>
    );
  }

  return (
    <button
      onClick={handleDownload}
      disabled={loading}
      className="flex items-center gap-2 px-3 py-2 bg-as-green text-as-black hover:bg-white transition-colors text-[10px] uppercase tracking-wider font-bold disabled:opacity-60"
      data-testid="pdf-download-btn"
    >
      {loading ? (
        <CircleNotch size={14} weight="bold" className="animate-spin" />
      ) : (
        <DownloadSimple size={14} weight="bold" />
      )}
      <span className="hidden sm:inline">{loading ? "Generando..." : "Descargar PDF"}</span>
    </button>
  );
}
